/**
 * ActiveAlertPanel — the live alerts for whatever profile is being viewed, shown
 * above that page's first details card.
 *
 * Used by DeviceProfilePage, AreaProfilePage and ResidentProfilePage. The caller
 * decides which alerts belong to it via alertsForDevice / alertsForArea /
 * alertsForResident in src/utils/activeAlerts.js; this component only renders.
 *
 * Rows arrive pre-normalized, so it is shape-agnostic: a 5.0 and a 4.0 alert
 * render identically. That is why MobileAlertCard is not reused here — it reads
 * cloud field names directly and would render blank on a 4.0 campus.
 *
 * One `scope` prop drives every difference between the pages, via the SCOPES table
 * below rather than a set of independent flags a caller could combine
 * incoherently. Each page omits whatever it already states about itself, so no
 * scope shows the assigned area name — every one of these pages has it in view
 * already — and only the resident scope shows the near-area, since a resident is
 * the one subject that moves around.
 *
 * The alert itself never links out. `/profiles/event/:id` reads
 * core_event_archive, which an alert only reaches once finished, so every row here
 * would 404 by definition. The device name and the near-area do link, since those
 * pages are live — the near-area only when the payload carried an id.
 *
 * Read-only otherwise. Accepting, finishing and acknowledging belong on the alerts
 * pages, which own the permission and identity rules for those actions
 * (CloudEmergencyTable.jsx renderRowActions). Duplicating them here would mean two
 * places to keep in step for no new capability.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AlertStatusBadge from '../../pages/events/alerts/components/AlertStatusBadge.jsx';
import { convertIsoUTCToLocal } from '../../libs.jsx';
import { getDeviceProfileLink } from '../../utils/deviceLinks.js';
import {
  utcToMs, formatDurationClock, formatSpanShort, isServerOfflineAlert,
} from '../../utils/activeAlerts.js';

/** UTC now in the same SQL shape the alert timestamps arrive in. */
const nowUtcSql = () => new Date().toISOString().slice(0, 19).replace('T', ' ');

/**
 * Device profile URL for an alert, or null when that device has no page.
 *
 * The shared device rule (utils/deviceLinks.js), fed from the normalized alert's
 * camelCase fields. It links wandering tags and skips Inovonics system types, which
 * the local 1-and-4-only map that was here did not (docs/DUPLICATION_AUDIT.md, B8).
 */
function deviceLinkFor(alert) {
  if (!alert?.deviceId) return null;
  /* Server-offline alerts are already excluded from device matching and device
     grouping (activeAlerts.js), so one should never reach this panel. Handled
     anyway, and before the manufacturer map: a server reports manufacture_id 4,
     so if one ever arrives here through an area or resident bucket the fallthrough
     would link its palcare_device_id to an unrelated device profile. Returning
     null renders the name as plain text — no role check needed, because a wrong
     link is worse than no link. */
  if (isServerOfflineAlert(alert)) return null;
  return getDeviceProfileLink({
    device_id: alert.deviceId,
    manufacture_id: alert.manufactureId,
    device_type_id: alert.deviceTypeId,
  });
}

/**
 * Re-renders once a second, but only while `active`.
 *
 * The interval exists solely to advance the duration readout, so it is torn down
 * whenever there is no alert — an idle profile page must not re-render every
 * second for the life of the page.
 */
function useSecondTick(active) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);

  return now;
}

/**
 * Campus-local start time, with the date shown only when it is not today.
 *
 * Both halves go through `convertIsoUTCToLocal` rather than a second piece of
 * offset arithmetic, so "is this today" can never disagree with the timestamp
 * printed beside it. An alert running across midnight keeps its date.
 */
function localTimeLabel(utc, tzOffset) {
  const full = utc ? convertIsoUTCToLocal(utc, tzOffset) : '';
  if (!full) return '';
  const [date, time] = full.split(' ');
  if (!time) return full;
  const today = (convertIsoUTCToLocal(nowUtcSql(), tzOffset) || '').split(' ')[0];
  return date === today ? time : full;
}

/**
 * What each page shows, and what it leaves out because it states it elsewhere.
 *
 *   device   — the device is the page; its header names the one assigned resident.
 *   area     — several devices and possibly several residents, so both are named.
 *   resident — the resident is the page, but they may carry more than one device,
 *              and they move, so the near-area is worth stating when present.
 */
const SCOPES = {
  device: { where: 'on this device', device: false, resident: false, near: false },
  area: { where: 'in this area', device: true, resident: true, near: false },
  resident: { where: 'for this resident', device: true, resident: false, near: true },
};

/* `variant`:
     banner  — the profile pages, sitting above their first details card.
     popover — inside AlertBellPopover on a list page. Same content, so the two can
               never drift; the modifier only drops the outer frame and the bottom
               margin, because the popover already provides a surface. */
const ActiveAlertPanel = ({ alerts, tzOffset, scope = 'device', variant = 'banner' }) => {
  const hasAlerts = Boolean(alerts?.length);
  const now = useSecondTick(hasAlerts);

  if (!hasAlerts) return null;

  const cfg = SCOPES[scope] || SCOPES.device;

  return (
    <div className={`pp-alert-panel${variant === 'popover' ? ' pp-alert-panel--popover' : ''}`}>
      {alerts.length > 1 ? (
        <p className="pp-alert-panel__count">
          <i className="fe fe-bell" aria-hidden="true" /> {alerts.length} active alerts {cfg.where}
        </p>
      ) : null}

      <ul className="pp-alert-panel__list">
        {alerts.map((a) => {
          /* Category and type are often the same word on 4.0; showing it twice
             reads like a rendering fault, so collapse the duplicate. */
          const label = [a.category, a.type].filter(Boolean)
            .filter((v, i, arr) => arr.indexOf(v) === i)
            .join(' / ') || 'Alert';

          const startedMs = utcToMs(a.startedUtc);
          const acceptedMs = utcToMs(a.acceptedUtc);
          /* An acceptance is only real once someone is named. A timestamp with no
             username, or the reverse, is partial data — treat it as not accepted
             rather than printing half a fact. */
          const accepted = Boolean(a.acceptedBy && acceptedMs);
          const responseMs = accepted && startedMs !== null ? acceptedMs - startedMs : null;
          const deviceHref = cfg.device ? deviceLinkFor(a) : null;

          return (
            <li key={a.key} className="pp-alert-panel__row">
              <div className="pp-alert-panel__head">
                <i className="fe fe-bell pp-alert-panel__icon" aria-hidden="true" />
                <span className="pp-alert-panel__label">{label}</span>
                <AlertStatusBadge status={a.status} eventDateTime={a.startedUtc} />
                {startedMs !== null ? (
                  <span
                    className="pp-alert-panel__duration"
                    title="Time since this alert started"
                  >
                    {formatDurationClock(now - startedMs)}
                  </span>
                ) : null}
              </div>

              {/* Same pp-field language as the details card below it, so the strip
                  reads as part of the page rather than a widget dropped onto it. */}
              <div className="pp-alert-panel__facts">
                {cfg.device ? (
                  <div className="pp-field">
                    <span className="pp-field__label">Device</span>
                    <span className="pp-field__value">
                      {deviceHref ? (
                        <Link to={deviceHref} className="pp-field__value--link">
                          {a.deviceName || 'Unnamed device'}
                        </Link>
                      ) : (a.deviceName || 'Unnamed device')}
                      {a.serial ? (
                        <span className="pp-alert-panel__serial">{a.serial}</span>
                      ) : null}
                    </span>
                  </div>
                ) : null}

                <div className="pp-field">
                  <span className="pp-field__label">Started</span>
                  <span className="pp-field__value">
                    {localTimeLabel(a.startedUtc, tzOffset) || '-'}
                  </span>
                </div>

                {/* Where the device was detected, which is not the same as where it
                    is assigned — the reason it earns a place on a page about
                    someone who moves. Hidden when the campus sends nothing. */}
                {cfg.near && a.nearAreaName ? (
                  <div className="pp-field">
                    <span className="pp-field__label">Near area</span>
                    <span className="pp-field__value">
                      {/* Linked only when an id came through, which in practice
                          means a 5.0 campus — 4.0 sends a near-area name with no
                          id at all. Same target and same guard as the Near Area
                          column on CloudAlertsPage. */}
                      {a.nearAreaId ? (
                        <Link to={`/profiles/area/${a.nearAreaId}`} className="pp-field__value--link">
                          {a.nearAreaName}
                        </Link>
                      ) : a.nearAreaName}
                    </span>
                  </div>
                ) : null}

                {cfg.resident && a.residentName ? (
                  <div className="pp-field">
                    <span className="pp-field__label">Resident</span>
                    <span className="pp-field__value">{a.residentName}</span>
                  </div>
                ) : null}

                {accepted ? (
                  <>
                    <div className="pp-field">
                      <span className="pp-field__label">Accepted by</span>
                      <span className="pp-field__value">{a.acceptedBy}</span>
                    </div>
                    <div className="pp-field">
                      <span className="pp-field__label">Accepted at</span>
                      <span className="pp-field__value">
                        {localTimeLabel(a.acceptedUtc, tzOffset) || '-'}
                        {/* Response time is the more useful of the two numbers, and
                            it is free to derive. Suppressed when the accept
                            timestamp precedes the start, which would otherwise
                            print a nonsensical "after 0s". */}
                        {responseMs !== null && responseMs >= 0 ? (
                          <span className="pp-alert-panel__aside">
                            {' '}(after {formatSpanShort(responseMs)})
                          </span>
                        ) : null}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="pp-field">
                    <span className="pp-field__label">Acceptance</span>
                    <span className="pp-field__value pp-alert-panel__unaccepted">
                      Not yet accepted
                    </span>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ActiveAlertPanel;
